import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import NotificationSettings from './NotificationSettings';
import './ManageAccount.css';

function ManageAccount() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  // Check subscription status when component mounts
  useEffect(() => {
    checkSubscriptionStatus();
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/subscription-status`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
        }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to check subscription status:', error);
      setSubscriptionStatus({ hasActiveSubscription: false });
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/create-portal-session`, {}, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Redirect to Stripe Customer Portal
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to create portal session:', error);
      setError('Unable to access subscription management. You may not have an active subscription.');
      setPortalLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match');
      return;
    }
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/change-password`, {
        currentPassword,
        newPassword
      }, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      setMessage(response.data.message);
      setError('');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      setError('Failed to change password');
      setMessage('');
    }
  };

  return (
    <div className="manage-account-container">
      <h2>Manage Account</h2>
      
      <div className="manage-account-card">
        <h3>Change Password</h3>
        {message && <div className="success">{message}</div>}
        {error && <div className="error">{error}</div>}
        <form onSubmit={handleChangePassword} className="manage-account-form">
          <div className="form-group">
            <label htmlFor="currentPassword">Current Password</label>
            <input
              id="currentPassword"
              type="password"
              placeholder="Enter your current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              id="newPassword"
              type="password"
              placeholder="Enter your new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmNewPassword">Confirm New Password</label>
            <input
              id="confirmNewPassword"
              type="password"
              placeholder="Confirm your new password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              required
            />
          </div>
          
          <button type="submit" className="manage-account-button">Change Password</button>
        </form>
      </div>

      <div className="manage-account-card">
        <h3>Subscription Management</h3>
        <p className="subscription-description">
          Manage your monthly subscription, update payment methods, view billing history, or cancel your subscription.
        </p>
        
        {subscriptionStatus && (
          <div className="subscription-status">
            {subscriptionStatus.hasActiveSubscription ? (
              <div className="subscription-active">
                <div className="status-indicator active">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <span className="status-text">Active monthly subscription</span>
              </div>
            ) : (
              <div className="subscription-inactive">
                <div className="status-indicator inactive">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                  </svg>
                </div>
                <span className="status-text">No active subscription</span>
              </div>
            )}
          </div>
        )}

        <button 
          onClick={handleManageSubscription}
          disabled={portalLoading || (subscriptionStatus && !subscriptionStatus.hasActiveSubscription)}
          className={`manage-account-button ${(!subscriptionStatus || !subscriptionStatus.hasActiveSubscription) ? 'disabled' : ''}`}
        >
          {portalLoading ? (
            <>
              <span className="spinner"></span>
              Opening Subscription Portal...
            </>
          ) : (
            'Manage Subscription'
          )}
        </button>
        
        {subscriptionStatus && !subscriptionStatus.hasActiveSubscription && (
          <p className="no-subscription-text">
            You don't have an active subscription. <a href="/credits">Purchase a monthly subscription</a> to access subscription management.
          </p>
        )}
      </div>

      <div className="manage-account-card">
        <NotificationSettings />
      </div>
    </div>
  );
}

export default ManageAccount;
